import React, { useCallback, useMemo } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import {
  TextField,
  FormControl,
  FormHelperText,
  Box,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useGetKubernetesClusters } from '../../hooks';
import { KubernetesCluster } from './types';
import { serializeClusterEntity } from './utils';

export const KubernetesClusterPicker = (
  props: FieldExtensionComponentProps<KubernetesCluster | string>,
) => {
  const {
    onChange,
    schema: { title = '', description = '', type = 'string' },
    required,
    rawErrors,
    formData,
    idSchema,
    uiSchema,
  } = props;

  const onlyUserOwned = useMemo(
    () => Boolean(uiSchema?.['ui:options']?.onlyUserOwned),
    [uiSchema],
  );

  const { value: entities, loading } = useGetKubernetesClusters(onlyUserOwned);

  const clusters = useMemo(
    () => entities?.map(entity => serializeClusterEntity(entity)) || [],
    [entities],
  );

  const onSelect = useCallback(
    (_: any, value: KubernetesCluster | null) => {
      if (!value) {
        onChange('');
        return;
      }

      if (type === 'object') {
        onChange(value as KubernetesCluster);
      } else {
        onChange(value.id);
      }
    },
    [onChange, type],
  );

  const selectedCluster = useMemo(() => {
    if (!formData) return null;

    if (typeof formData === 'string') {
      return clusters?.find(e => e.id === formData) || null;
    }

    if (typeof formData === 'object' && formData) {
      return clusters?.find(e => e.name === (formData as any).name) || null;
    }
    return null;
  }, [formData, clusters]);

  return (
    <FormControl
      fullWidth
      required={required}
      error={rawErrors?.length > 0 && !formData}
    >
      <Autocomplete
        size="small"
        id={idSchema?.$id}
        value={selectedCluster}
        loading={loading}
        onChange={onSelect}
        options={clusters}
        getOptionLabel={option => option.title}
        renderInput={params => (
          <TextField
            {...params}
            label={title}
            margin="none"
            color="secondary"
            helperText={description}
            variant="outlined"
            required={required}
            InputProps={params.InputProps}
            error={Boolean(rawErrors?.length > 0)}
          />
        )}
        renderOption={option => (
          <Box>
            <Typography variant="body2" noWrap>
              {option.title}
            </Typography>
            {option.subscriptionId && (
              <Typography
                variant="caption"
                noWrap
                color="textSecondary"
                style={{ display: 'block' }}
              >
                Subscription ID: {option.subscriptionId}
              </Typography>
            )}
            {option.resourceGroup && (
              <Typography
                variant="caption"
                noWrap
                color="textSecondary"
                style={{ display: 'block' }}
              >
                Resource Group: {String(option.resourceGroup)}
              </Typography>
            )}
          </Box>
        )}
        noOptionsText="No clusters found"
        loadingText="Loading clusters..."
      />
      {rawErrors?.map((err, index) => (
        <FormHelperText key={index} error>
          {err}
        </FormHelperText>
      ))}
    </FormControl>
  );
};
